import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { BiSolidMessageSquareAdd, BiSolidNotepad } from "react-icons/bi";
import { RiQuoteText } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useTranslation } from "react-i18next";
import InvListBasic from "../../components/quote/InvListBasic";
import SearchQuote from "../../components/quote/SearchQuote";
import CardHeadeWithActions from "../../components/ui/CardHeadeWithActions";
import { MwButton } from "../../components/ui";
import { useCurrent, useFetch } from "../../hooks";
import withGuard from "../../utils/withGuard";

const DEFAULT_ITEMS_PER_PAGE = 13;

const ManageQuotes = () => {
    const params = useParams();
    const { t, i18n } = useTranslation("global");
    const { currentTenantId, currentCompanyId, currentBranchId } = useCurrent();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);

    const baseUrl = `${process.env.REACT_APP_INV_API_SALES_SERVER_BASE_URL}/taxes/${currentTenantId}/${currentCompanyId}/${currentBranchId}`;
    const { data: taxesData } = useFetch(baseUrl);
    const currenciesBaseUrl = `${process.env.REACT_APP_INV_API_SALES_SERVER_BASE_URL}/currencies/${currentTenantId}/${currentCompanyId}`;
    const { data: currenciesData } = useFetch(currenciesBaseUrl);

    const totalPages = Math.ceil(quotes.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = quotes.slice(indexOfFirstItem, indexOfLastItem);
    const isShowingAll = itemsPerPage !== DEFAULT_ITEMS_PER_PAGE;

    const fetchQuotes = async ({ resetPage = false } = {}) => {
        if (!currentTenantId || !currentCompanyId || !currentBranchId) {
            return;
        }

        if (resetPage) {
            setCurrentPage(1);
        }

        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/quotes/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
            );
            setQuotes(response?.data?.data || []);
        } catch (error) {
            setQuotes([]);
        } finally {
            setLoading(false);
        }
    };

    const onChangeSearch = (newValue) => {
        if (Array.isArray(newValue) && newValue.length > 0) {
            setCurrentPage(1);
            setItemsPerPage(DEFAULT_ITEMS_PER_PAGE);
            setQuotes(newValue);
            return;
        }

        fetchQuotes({ resetPage: true });
    };

    useEffect(() => {
        fetchQuotes();
    }, [currentTenantId, currentCompanyId, currentBranchId]);

    useEffect(() => {
        if (taxesData) {
            localStorage.setItem("taxesData", JSON.stringify(taxesData));
        }
    }, [taxesData]);

    useEffect(() => {
        if (!quotes.length) {
            setCurrentPage(1);
            return;
        }

        const availablePages = Math.max(1, Math.ceil(quotes.length / itemsPerPage));
        if (currentPage > availablePages) {
            setCurrentPage(availablePages);
        }
    }, [quotes.length, itemsPerPage, currentPage]);

    return (
        <div className="flex flex-col gap-6 pb-3">
            <CardHeadeWithActions
                title={t("quotes.manageQuotes")}
                icon={<BiSolidNotepad size={24} />}
            >
                <Link to={`/${params.tenant}/quotes/create`}>
                    <MwButton
                        inGroup={true}
                        type="saveBtn"
                        classNameCustom="flex items-center gap-2 transition-all duration-200 hover:shadow-lg"
                    >
                        <BiSolidMessageSquareAdd size={18} />
                        {t("quotes.createNewQuote")}
                    </MwButton>
                </Link>
            </CardHeadeWithActions>

            <div className="mt-[-10px] px-3">
                <div className=" rounded-3xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 p-6 shadow-md transition-shadow duration-300 hover:shadow-lg">
                    <div className="flex flex-col gap-5">
                        <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <div className="text-base font-semibold text-[#251658]">
                                    {t("quotes.quotesList")}
                                </div>
                                <div className="mt-1 text-xs text-slate-400">
                                    {quotes.length > 0
                                        ? `${quotes.length} ${t("quotes.quotesList")}`
                                        : t("DashBoard.noQuotes")}
                                </div>
                            </div>

                            <div className="w-full lg:max-w-sm">
                                <SearchQuote
                                    className="border border-slate-200 shadow-sm"
                                    onChangeSearch={onChangeSearch}
                                    isOpen={false}
                                />
                            </div>
                        </div>

                        <div className="  border border-slate-100 bg-white">
                            <InvListBasic
                                loading={loading}
                                withBorder={false}
                                data={currentItems}
                                currencies={currenciesData?.data || []}
                            />
                        </div>

                        {totalPages > 1 && (
                            <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                                <button
                                    onClick={() => {
                                        setCurrentPage(1);
                                        setItemsPerPage(quotes.length || DEFAULT_ITEMS_PER_PAGE);
                                    }}
                                    className={`min-w-[48px] rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                                        isShowingAll
                                            ? "bg-indigo-600 text-white"
                                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                    }`}
                                >
                                    {t("common.all")}
                                </button>

                                {totalPages > 5 && currentPage > 3 && (
                                    <button
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        className="rounded-xl bg-slate-100 p-2 text-slate-600 transition-colors hover:bg-slate-200"
                                    >
                                        {i18n.language === "ar" ? (
                                            <MdKeyboardArrowRight size={20} />
                                        ) : (
                                            <MdKeyboardArrowLeft size={20} />
                                        )}
                                    </button>
                                )}

                                {Array.from({ length: totalPages }, (_, index) => index + 1)
                                    .filter((pageNumber) => {
                                        if (totalPages <= 5) {
                                            return true;
                                        }

                                        if (pageNumber === 1 || pageNumber === totalPages) {
                                            return true;
                                        }

                                        return Math.abs(currentPage - pageNumber) < 3;
                                    })
                                    .map((pageNumber) => (
                                        <button
                                            key={pageNumber}
                                            onClick={() => {
                                                setCurrentPage(pageNumber);
                                                setItemsPerPage(DEFAULT_ITEMS_PER_PAGE);
                                            }}
                                            className={`min-w-[44px] rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                                                currentPage === pageNumber && !isShowingAll
                                                    ? "bg-indigo-600 text-white"
                                                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                            }`}
                                        >
                                            {pageNumber}
                                        </button>
                                    ))}

                                {totalPages > 5 && currentPage < totalPages - 2 && (
                                    <button
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        className="rounded-xl bg-slate-100 p-2 text-slate-600 transition-colors hover:bg-slate-200"
                                    >
                                        {i18n.language === "ar" ? (
                                            <MdKeyboardArrowLeft size={20} />
                                        ) : (
                                            <MdKeyboardArrowRight size={20} />
                                        )}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withGuard(ManageQuotes);
